import { scoreBand } from "../lib/portfolioStatus";
import { controlRefWithStandard } from "../lib/controlRef";
import { useEffect, useState } from "react";
import {
  Search, ClipboardList, Shield, Bot, BadgeCheck, LockKeyhole,
  CreditCard, Scale, FileText, CalendarDays, Building2,
  Clock, Ban, AlertTriangle, FilePen,
} from "lucide-react";
import { usePortalNavigate } from "../hooks/usePortalNavigate";
import { useMobile } from "../context/MobileContext";
import { theme, card, btnPrimary, btnSecondary } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks, AI_ASSESSABLE_FRAMEWORKS, FRAMEWORK_LABELS as FRAMEWORK_NAMES } from "../context/FrameworksContext";
import type { FrameworkId } from "../context/FrameworksContext";
import { controlRoute } from "../utils/controlRoute";
import { FrameworkFilter, FRAMEWORK_COLORS } from "../components/FrameworkFilter";
import { countBySeverity, OPEN_RISK_STATUSES, SEVERITY_ORDER, SEVERITY_META } from "../lib/riskScoring";
import { ActionsTile } from "../components/ActionsTile";
import { RulerProgress, StatusTag, type StatusTone } from "../components/Drawing";
import { approvedView, type SoaApprovalEntry } from "../lib/soaApproval";

import { cbSafe } from "../lib/colourBlind";
// ── Interfaces ────────────────────────────────────────────────────────────

interface SoaRow {
  control: { id: string; frameworkId: string; kind: "CLAUSE" | "CONTROL"; name: string };
  entry: SoaApprovalEntry;
}
interface CheckRun {
  id: string;
  status: "queued" | "running" | "done" | "error";
  overallScore: number | null;
  createdAt: string;
  completedAt: string | null;
}
interface Finding {
  id: string;
  controlId: string;
  frameworkId?: string | null;
  text: string;
  status: string;
  createdAt?: string;
}
interface ScheduleEvent {
  id: string;
  title: string;
  eventDate: string;
  kind: string;
  notes?: string | null;
  completedAt?: string | null;
}
interface OrgBasic {
  name: string;
  currentMonthSpendPence: number;
  spendCapPence: number;
  serviceTier: { level: number; basePlatformFeePence: number; name: string; description: string } | null;
  bureau: { name: string; accreditation: string | null; certFeePence: number | null; annualFeePence: number | null } | null;
}

interface UsageBilling {
  standardsFeePence: number;
  tierAddonPence: number;
  integrationAddonPence: number;
  tprmAddonPence: number;
  platformFeePence: number;
  currentMonthSpendPence: number;
  spendCapPence: number;
}
// The Cyber Essentials requirements are SOA rows (frameworkId "cyber_essentials"), shared by CE and CE+.
const CE_ROWS = "cyber_essentials";
interface LegalSummary { total: number; compliant: number; gap: number; inProgress: number; notStarted: number; regulations: string[]; }

// ── KPI card descriptor ───────────────────────────────────────────────────

interface KpiCard {
  label: string;
  value: string;
  sub: string;
  color: string;
  small?: boolean;
  link?: string;
  linkLabel?: string;
}

// ── Constants ─────────────────────────────────────────────────────────────


const FINDING_STATUS: Record<string, { label: string; color: string; bg: string; border: string }> = {
  open:         { label: "Needs Work",  color: "var(--c-warningText)", bg: "var(--c-warningBg)", border: "var(--c-warning)" },
  acknowledged: { label: "Observation", color: "var(--c-accentText)",  bg: "var(--c-accentBg)",  border: "var(--c-accent)" },
  resolved:     { label: "Approved",    color: "var(--c-successText)", bg: "var(--c-successBg)", border: "var(--c-success)" },
};

const KIND_LABEL: Record<string, string> = {
  review: "Review", audit: "Audit", renewal: "Renewal", meeting: "Meeting",
};

function fmtShortDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function daysLate(iso: string): number {
  const diff = Date.now() - new Date(iso).getTime();
  return Math.floor(diff / 86_400_000);
}

// ── Quick-action definitions (framework-aware) ────────────────────────────

interface QuickAction {
  icon: React.ReactNode;
  label: string;
  sub: string;
  to: string;
}

const IASME_TIERS: Array<{ id: FrameworkId; label: string; route: string }> = [
  { id: "iasme_ca_sole_trader",  label: "IASME CA (Sole Trader)",  route: "/iasme-ca-sole-trader" },
  { id: "iasme_ca_micro",        label: "IASME CA (Micro)",        route: "/iasme-ca-micro" },
  { id: "iasme_ca_small",        label: "IASME CA (Small)",        route: "/iasme-ca-small" },
  { id: "iasme_ca_medium_large", label: "IASME CA (Medium/Large)", route: "/iasme-ca-medium-large" },
];

const PCI_SAQ_LABELS_QA: Record<string, string> = {
  "saq-a": "SAQ A", "saq-a-ep": "SAQ A-EP", "saq-b": "SAQ B", "saq-b-ip": "SAQ B-IP",
  "saq-c": "SAQ C", "saq-c-vt": "SAQ C-VT", "saq-p2pe": "SAQ P2PE",
  "saq-d-merchant": "SAQ D (Merchant)", "saq-d-sp": "SAQ D (Service Provider)",
};

function getQuickActions(enrolled: Set<FrameworkId>, pciSaqTypes: string[], hasLegal: boolean): QuickAction[] {
  const actions: QuickAction[] = [];

  // The AI check covers every enrolled standard it can assess — not just ISO 27001.
  const assessable = [...enrolled].filter((id) => AI_ASSESSABLE_FRAMEWORKS.has(id));
  if (assessable.length > 0) {
    const names = assessable.map((id) => FRAMEWORK_NAMES[id]);
    const against = names.length <= 2 ? names.join(" and ") : `your ${names.length} enrolled standards`;
    actions.push({ icon: <Search size={15} />, label: "Check my compliance", sub: `See how you measure up against ${against}`, to: "/compliance" });
  }
  if (enrolled.has("iso27001")) {
    actions.push({ icon: <ClipboardList size={15} />, label: "Statement of Applicability", sub: "Confirm which Annex A controls apply to you", to: "/iso27001-soa" });
  }
  if (enrolled.has("cyber_essentials") || enrolled.has("cyber_essentials_plus")) {
    const ceTo = enrolled.has("cyber_essentials_plus") ? "/cyber-essentials-plus" : "/cyber-essentials";
    actions.push({ icon: <Shield size={15} />, label: "Cyber Essentials self-assessment", sub: "Update your control status across all five themes", to: ceTo });
  }
  if (enrolled.has("iso42001")) {
    actions.push({ icon: <Bot size={15} />, label: "AI management controls", sub: "Review your ISO 42001 Annex A controls", to: "/iso42002" });
  }
  if (enrolled.has("iso9001")) {
    actions.push({ icon: <BadgeCheck size={15} />, label: "Quality management clauses", sub: "Review your ISO 9001 compliance position", to: "/iso9001" });
  }
  if (enrolled.has("soc2")) {
    actions.push({ icon: <LockKeyhole size={15} />, label: "Trust service criteria", sub: "Review your SOC 2 compliance position", to: "/soc2" });
  }
  if (enrolled.has("pci_dss")) {
    actions.push({
      icon: <CreditCard size={15} />,
      label: "PCI DSS requirements",
      sub: pciSaqTypes.length > 0 ? `Review your ${pciSaqTypes.map((t) => PCI_SAQ_LABELS_QA[t]).join(", ")} requirements` : "Select your SAQ type(s) to get started",
      to: pciSaqTypes.length > 0 ? `/pci-dss/${pciSaqTypes[0]}` : "/pci-dss/saq-a",
    });
  }

  for (const tier of IASME_TIERS) {
    if (enrolled.has(tier.id)) {
      actions.push({ icon: <Shield size={15} />, label: `${tier.label} assessment`, sub: "Review your IASME Cyber Assurance themes", to: tier.route });
    }
  }

  if (hasLegal) {
    actions.push({ icon: <Scale size={15} />, label: "Legal obligations", sub: "Review your regulatory compliance position", to: "/legal" });
  }

  // Always-present actions
  actions.push({ icon: <FileText size={15} />, label: "Get a policy template", sub: "Download a ready-to-use policy document", to: "/templates" });
  actions.push({ icon: <CalendarDays size={15} />, label: "See upcoming reviews", sub: "View your review deadlines and schedule", to: "/schedule" });
  actions.push({ icon: <Building2 size={15} />, label: "Set up my organisation", sub: "Fill in your details to personalise templates", to: "/org-profile" });

  return actions;
}

// ── Dashboard ─────────────────────────────────────────────────────────────

export function DashboardPage() {
  const navigate = usePortalNavigate();
  const isMobile = useMobile();
  const { frameworks, enrolled, pciSaqTypes, loading: frameworksLoading } = useFrameworks();
  const [hiddenFw, setHiddenFw] = useState<Set<FrameworkId>>(new Set());

  const [soa, setSoa]           = useState<SoaRow[]>([]);
  const [soaLoaded, setSoaLoaded] = useState(false);
  const [runs, setRuns]         = useState<CheckRun[]>([]);
  const [findings, setFindings] = useState<Finding[]>([]);
  const [events, setEvents]     = useState<ScheduleEvent[]>([]);
  const [orgData, setOrgData]             = useState<OrgBasic | null>(null);
  const [consultancySpendPence, setConsultancySpendPence] = useState<number>(0);
  const [usageBilling, setUsageBilling] = useState<UsageBilling | null>(null);
  const [legalSummary, setLegalSummary] = useState<LegalSummary | null>(null);
  const [riskSummary, setRiskSummary] = useState<Array<{ residualScore?: number | null; inherentScore?: number | null }>>([]);
  const [tprmSummary, setTprmSummary] = useState<{ total: number; highCritical: number; overdueReviews: number; awaitingReview: number } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { user } = useCurrentUser();
  // Fees and spend are billing:view (GET /usage); other members get no Billing & Usage card.
  const canBilling = !!user?.permissions?.includes("billing:view");
  useEffect(() => {
    if (canBilling) api.get<UsageBilling>("/usage").then(setUsageBilling).catch(() => {});
  }, [canBilling]);
  const [barsVisible, setBarsVisible] = useState(false);

  useEffect(() => {
    // Fire all secondary fetches immediately — do NOT block on SOA loading.
    api.get<{ organization: OrgBasic }>("/organizations/profile")
      .then((d) => setOrgData(d.organization)).catch(() => {});


    Promise.all([
      api.get<{ totalPence: number }>("/billing/consultancy-bookings").catch(() => ({ totalPence: 0 })),
      api.get<{ slots: Array<{ status: string; costPence: number }> }>("/billing/slots").catch(() => ({ slots: [] })),
    ]).then(([bookings, slotsData]) => {
      const bookingCost = bookings.totalPence ?? 0;
      const slotCost = (slotsData.slots ?? [])
        .filter((s) => s.status === "confirmed" || s.status === "pending")
        .reduce((sum, s) => sum + s.costPence, 0);
      setConsultancySpendPence(bookingCost + slotCost);
    }).catch(() => {});

    api.get<{ findings: Finding[] }>("/findings").then((d) => setFindings(d.findings ?? [])).catch(() => {});
    // Open risks only (closed and accepted ones are not exposure), as the register, the board report
    // and Analytics count them.
    api.get<{ risks: Array<{ residualScore?: number | null; inherentScore?: number | null; status?: string }> }>("/risks")
      .then((d) => setRiskSummary((d.risks ?? []).filter((r) => !r.status || OPEN_RISK_STATUSES.has(r.status)))).catch(() => {});
    api.get<LegalSummary>("/legal/summary").then((d) => setLegalSummary(d)).catch(() => {});
    // The supplier summary only when the TPRM add-on is on: without it the route refuses (403).
    api.get<{ addons: { tprm?: boolean } }>("/organizations/addons")
      .then((a) => a.addons?.tprm
        ? api.get<{ total: number; highCritical: number; overdueReviews: number; awaitingReview: number }>("/tprm/summary").then((d) => setTprmSummary(d))
        : undefined)
      .catch(() => {});
    api.get<{ events: ScheduleEvent[] }>("/schedule").then((d) => setEvents(d.events ?? [])).catch(() => {});

    // SOA + compliance runs are blocking (KPI cards depend on them).
    async function load() {
      try {
        const [soaData, runsData] = await Promise.all([
          api.get<{ soa: SoaRow[] }>("/controls/soa"),
          api.get<{ runs: CheckRun[] }>("/compliance/check-runs").catch(() => ({ runs: [] as CheckRun[] })),
        ]);
        setSoa(soaData.soa);
        setRuns(runsData.runs);
      } catch (e: unknown) {
        const msg = e && typeof e === "object" && "message" in e ? String((e as { message: unknown }).message) : "Unknown error";
        setLoadError(msg);
      } finally {
        setSoaLoaded(true);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!soaLoaded) return;
    const t = setTimeout(() => setBarsVisible(true), 150);
    return () => clearTimeout(t);
  }, [soaLoaded]);

  if (!soaLoaded) return <LoadingBlock />;

  // ── Derived values ────────────────────────────────────────────────────

  // A change waiting for consultant approval does not move a figure: every count reads the approved values.
  const compliantOf = (rows: SoaRow[]) => rows.filter((r) => approvedView(r.entry).status === "COMPLIANT").length;

  // The score definition of backend/src/lib/frameworkScores.ts: every catalogue control this
  // member can see (GET /controls/soa already applies role and department filters), less the
  // ones marked not applicable. A control never assessed stays in the denominator. The sidebar
  // score chip is computed the same way, so the two always agree.
  const scored = soa.filter((r) => approvedView(r.entry).applicable !== false);

  const iso27Mandatory = scored.filter((r) => r.control.frameworkId === "iso27001" && r.control.kind === "CLAUSE");
  const iso27AnnexA    = scored.filter((r) => r.control.frameworkId === "iso27001" && r.control.kind === "CONTROL");
  const iso9001All     = scored.filter((r) => r.control.frameworkId === "iso9001");
  const iso42Mandatory = scored.filter((r) => r.control.frameworkId === "iso42001" && r.control.kind === "CLAUSE");
  const iso42AnnexB    = scored.filter((r) => r.control.frameworkId === "iso42001" && r.control.kind === "CONTROL");
  // Scored like every other standard (the sidebar chip uses the same rows), over the shared CE rows.
  const ceRows         = scored.filter((r) => r.control.frameworkId === CE_ROWS);
  const ceCount        = compliantOf(ceRows);
  const CE_TOTAL       = ceRows.length;

  const PCI_SAQ_REQUIREMENTS: Record<string, number[]> = {
    "saq-a":          [2, 6, 8, 9, 10, 12],
    "saq-a-ep":       [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    "saq-b":          [1, 2, 6, 8, 9, 10, 11, 12],
    "saq-b-ip":       [1, 2, 4, 6, 7, 8, 9, 10, 11, 12],
    "saq-c":          [1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    "saq-c-vt":       [1, 2, 4, 5, 6, 8, 9, 10, 11, 12],
    "saq-p2pe":       [3, 9, 10, 12],
    "saq-d-merchant": [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
    "saq-d-sp":       [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  };
  const PCI_SAQ_LABELS: Record<string, string> = {
    "saq-a": "SAQ A", "saq-a-ep": "SAQ A-EP", "saq-b": "SAQ B", "saq-b-ip": "SAQ B-IP",
    "saq-c": "SAQ C", "saq-c-vt": "SAQ C-VT", "saq-p2pe": "SAQ P2PE",
    "saq-d-merchant": "SAQ D (Merchant)", "saq-d-sp": "SAQ D (Service Provider)",
  };
  const pciAllRows = scored.filter((r) => r.control.frameworkId === "pci_dss" && r.control.kind === "CLAUSE");
  const pciReqsUnion = pciSaqTypes.length > 0
    ? new Set(pciSaqTypes.flatMap((t) => PCI_SAQ_REQUIREMENTS[t] ?? []))
    : null;
  const pciRows = pciReqsUnion
    ? pciAllRows.filter((r) => {
        const m = r.control.id.match(/pci-req-(\d+)$/);
        const n = m ? parseInt(m[1], 10) : null;
        return n !== null && pciReqsUnion.has(n);
      })
    : pciAllRows;

  const latestRun  = runs.find((r) => r.status === "done") ?? null;
  const pendingRun = runs.find((r) => r.status === "queued" || r.status === "running") ?? null;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const in30  = new Date(); in30.setDate(in30.getDate() + 30);
  // A task marked done on the calendar is history, not work (same rule as My Actions).
  const openEvents     = events.filter((e) => !e.completedAt);
  const overdueEvents  = openEvents.filter((e) => new Date(e.eventDate) < today);
  const upcomingEvents = openEvents.filter((e) => { const d = new Date(e.eventDate); return d >= today && d <= in30; });

  // Billing figures come from the server (/usage) so they match invoices exactly.
  const fwCount           = frameworks.length;
  const standardsFeePence = usageBilling?.standardsFeePence ?? 0;
  const tierFeePence      = usageBilling?.tierAddonPence ?? 0;
  const integrationFeePence = usageBilling?.integrationAddonPence ?? 0;
  const tprmFeePence        = usageBilling?.tprmAddonPence ?? 0;
  const platformFeePence  = usageBilling?.platformFeePence ?? 0;
  const aiSpendPence      = usageBilling?.currentMonthSpendPence ?? 0;
  const spendCapPence     = usageBilling?.spendCapPence ?? 0;
  const capHit = spendCapPence > 0 && aiSpendPence >= spendCapPence;

  const scoreColor = (s: number) => s >= 80 ? cbSafe("#22c55e") : s >= 50 ? "#f59e0b" : cbSafe("#dc2626");

  const fwVisible = (id: FrameworkId) => enrolled.has(id) && !hiddenFw.has(id);
  const ceVisible = fwVisible("cyber_essentials") || fwVisible("cyber_essentials_plus");

  // ── KPI cards ─────────────────────────────────────────────────────────

  const kpis: KpiCard[] = [];

  if (fwVisible("iso27001")) {
    const score = latestRun?.overallScore ?? null;
    kpis.push({
      // The AI compliance check's own figure, not the SOA score the sidebar chip and bars show.
      label: "Last AI check score",
      value: score != null ? `${score}%` : "--",
      sub: "from the last compliance check",
      color: score != null ? scoreColor(score) : "#3b82f6",
      link: "/compliance",
    });
    kpis.push({
      label: "Mandatory clauses met",
      value: `${compliantOf(iso27Mandatory)} / ${iso27Mandatory.length || "—"}`,
      sub: "ISO 27001:2022 Clauses 4–10",
      color: FRAMEWORK_COLORS["iso27001"],
      link: "/mandatory-clauses",
    });
    kpis.push({
      label: "Annex A controls met",
      value: `${compliantOf(iso27AnnexA)} / ${iso27AnnexA.length || "—"}`,
      sub: "ISO 27002:2022 A.5–A.8",
      color: FRAMEWORK_COLORS["iso27001"],
      link: "/annex-a",
    });
  }

  if (ceVisible) {
    const ceFw: FrameworkId = enrolled.has("cyber_essentials_plus") ? "cyber_essentials_plus" : "cyber_essentials";
    kpis.push({
      label: "CE controls compliant",
      value: `${ceCount} / ${CE_TOTAL}`,
      sub: "individual Cyber Essentials controls",
      color: FRAMEWORK_COLORS[ceFw],
      link: enrolled.has("cyber_essentials_plus") ? "/cyber-essentials-plus" : "/cyber-essentials",
    });
  }

  if (fwVisible("iso42001")) {
    kpis.push({
      label: "AIMS clauses met",
      value: `${compliantOf(iso42Mandatory)} / ${iso42Mandatory.length || "—"}`,
      sub: "ISO 42001:2023 Clauses 4–10",
      color: FRAMEWORK_COLORS["iso42001"],
      link: "/iso42001",
    });
    kpis.push({
      label: "AIMS controls met",
      value: `${compliantOf(iso42AnnexB)} / ${iso42AnnexB.length || "—"}`,
      sub: "ISO 42001 Annex A",
      color: FRAMEWORK_COLORS["iso42001"],
      link: "/iso42002",
    });
  }

  if (fwVisible("iso9001")) {
    kpis.push({
      label: "QMS clauses met",
      value: `${compliantOf(iso9001All)} / ${iso9001All.length || "—"}`,
      sub: "ISO 9001:2015 Clauses 4–10",
      color: FRAMEWORK_COLORS["iso9001"],
      link: "/iso9001",
    });
  }

  if (fwVisible("soc2")) {
    kpis.push({
      label: "Trust service criteria",
      value: "5",
      sub: "Security required · 4 optional",
      color: FRAMEWORK_COLORS["soc2"],
      link: "/soc2",
    });
  }

  if (fwVisible("pci_dss")) {
    if (pciSaqTypes.length > 0) {
      kpis.push({
        label: "PCI DSS requirements met",
        value: `${compliantOf(pciRows)} / ${pciRows.length || "—"}`,
        sub: `${pciSaqTypes.map((t) => PCI_SAQ_LABELS[t] ?? t).join(", ")} · ${pciRows.length} requirements in scope`,
        color: FRAMEWORK_COLORS["pci_dss"],
        link: `/pci-dss/${pciSaqTypes[0]}`,
      });
    } else {
      kpis.push({
        label: "PCI DSS",
        value: "—",
        sub: "Select your SAQ type to track requirements",
        color: FRAMEWORK_COLORS["pci_dss"],
        link: "/pci-dss/saq-a",
      });
    }
  }

  for (const tier of IASME_TIERS) {
    if (fwVisible(tier.id)) {
      const tierRows = scored.filter((r) => r.control.frameworkId === tier.id);
      kpis.push({
        label: "IASME CA themes met",
        value: `${compliantOf(tierRows)} / ${tierRows.length || "—"}`,
        sub: `${tier.label} · 14 themes`,
        color: FRAMEWORK_COLORS[tier.id],
        link: tier.route,
      });
    }
  }

  if (legalSummary && legalSummary.regulations.length > 0) {
    const legalPct = legalSummary.total > 0 ? Math.round((legalSummary.compliant / legalSummary.total) * 100) : 0;
    kpis.push({
      label: "Legal obligations met",
      value: `${legalSummary.compliant} / ${legalSummary.total || "—"}`,
      sub: `${legalSummary.regulations.length} regulation${legalSummary.regulations.length !== 1 ? "s" : ""} · ${legalPct}% compliant`,
      color: legalPct >= 80 ? cbSafe("#22c55e") : legalPct >= 50 ? "#f59e0b" : "#0288d1",
      link: "/legal",
    });
  }

  if (tprmSummary && tprmSummary.total > 0) {
    const tprmIssues = tprmSummary.overdueReviews + tprmSummary.awaitingReview;
    kpis.push({
      label: "Supplier posture",
      // Active and under review only (GET /tprm/summary), so the figure says so: the Suppliers
      // page also lists prospective and offboarded ones (AUD-46).
      value: `${tprmSummary.total} active supplier${tprmSummary.total !== 1 ? "s" : ""}`,
      sub: tprmIssues > 0
        ? `${tprmIssues} need${tprmIssues !== 1 ? "" : "s"} attention · ${tprmSummary.highCritical} high/critical`
        : `${tprmSummary.highCritical} high/critical · all reviews current`,
      color: tprmIssues > 0 ? cbSafe("#dc2626") : cbSafe("#22c55e"),
      link: "/tprm",
      linkLabel: "Open TPRM",
    });
  }


  // Operational
  kpis.push({
    label: "Overdue reviews",
    value: String(overdueEvents.length),
    sub: overdueEvents.length > 0 ? "need your attention" : "all up to date",
    color: overdueEvents.length > 0 ? cbSafe("#dc2626") : cbSafe("#22c55e"),
  });
  kpis.push({
    label: "Coming up",
    value: String(upcomingEvents.length),
    sub: "due in 30 days",
    color: upcomingEvents.length > 0 ? "#ca8a04" : "#64748b",
  });

  if (orgData?.bureau) {
    const b = orgData.bureau;
    const monthlyPence = b.annualFeePence ? Math.round(b.annualFeePence / 12) : (b.certFeePence ?? null);
    const bureauSub = [
      b.accreditation,
      monthlyPence ? `£${(monthlyPence / 100).toFixed(2)}/mo (cert fee incl.)` : null,
    ].filter(Boolean).join(" · ");
    kpis.push({
      label: "Assessment bureau",
      value: b.name,
      sub: bureauSub,
      color: "#94a3b8",
      small: true,
    });
  }

  // Sorted findings: needs_work first, then acknowledged, then resolved
  const findingOrder: Record<string, number> = { open: 0, acknowledged: 1, resolved: 2 };
  const sortedFindings = [...findings].sort((a, b) =>
    (findingOrder[a.status] ?? 3) - (findingOrder[b.status] ?? 3)
  );
  const actionCount = findings.filter((f) => f.status === "open").length;

  const quickActions = getQuickActions(enrolled, pciSaqTypes, !!(legalSummary && legalSummary.regulations.length > 0));

  // ── Compliance bars ────────────────────────────────────────────────────
  interface ComplianceBar { label: string; color: string; pct: number; detail: string; }
  const complianceBars: ComplianceBar[] = [];
  if (enrolled.has("iso27001") && iso27Mandatory.length > 0)
    complianceBars.push({ label: "ISO 27001 Clauses", color: "#6366F1", pct: Math.round(compliantOf(iso27Mandatory) / iso27Mandatory.length * 100), detail: `${compliantOf(iso27Mandatory)} of ${iso27Mandatory.length}` });
  if (enrolled.has("iso27001") && iso27AnnexA.length > 0)
    complianceBars.push({ label: "Annex A controls", color: "#8B5CF6", pct: Math.round(compliantOf(iso27AnnexA) / iso27AnnexA.length * 100), detail: `${compliantOf(iso27AnnexA)} of ${iso27AnnexA.length}` });
  if ((enrolled.has("cyber_essentials") || enrolled.has("cyber_essentials_plus")) && CE_TOTAL > 0)
    complianceBars.push({ label: enrolled.has("cyber_essentials_plus") ? "Cyber Essentials Plus" : "Cyber Essentials", color: cbSafe("#10B981"), pct: Math.round(ceCount / CE_TOTAL * 100), detail: `${ceCount} of ${CE_TOTAL}` });
  if (enrolled.has("iso42001") && iso42Mandatory.length > 0)
    complianceBars.push({ label: "ISO 42001 Clauses", color: "#8B5CF6", pct: Math.round(compliantOf(iso42Mandatory) / iso42Mandatory.length * 100), detail: `${compliantOf(iso42Mandatory)} of ${iso42Mandatory.length}` });
  if (enrolled.has("iso9001") && iso9001All.length > 0)
    complianceBars.push({ label: "ISO 9001", color: "#3B82F6", pct: Math.round(compliantOf(iso9001All) / iso9001All.length * 100), detail: `${compliantOf(iso9001All)} of ${iso9001All.length}` });
  if (enrolled.has("pci_dss") && pciRows.length > 0)
    complianceBars.push({ label: `PCI DSS${pciSaqTypes.length > 0 ? ` (${pciSaqTypes.map((t) => PCI_SAQ_LABELS[t] ?? t).join(", ")})` : ""}`, color: cbSafe("#EF4444"), pct: Math.round(compliantOf(pciRows) / pciRows.length * 100), detail: `${compliantOf(pciRows)} of ${pciRows.length}` });
  for (const tier of IASME_TIERS) {
    const tierRows = scored.filter((r) => r.control.frameworkId === tier.id);
    if (enrolled.has(tier.id) && tierRows.length > 0)
      complianceBars.push({ label: tier.label, color: FRAMEWORK_COLORS[tier.id], pct: Math.round(compliantOf(tierRows) / tierRows.length * 100), detail: `${compliantOf(tierRows)} of ${tierRows.length}` });
  }
  if (legalSummary && legalSummary.total > 0)
    complianceBars.push({ label: "Legal Obligations", color: "#0EA5E9", pct: Math.round((legalSummary.compliant / legalSummary.total) * 100), detail: `${legalSummary.compliant} of ${legalSummary.total}` });

  // ── Pill helper ────────────────────────────────────────────────────────
  function kpiPill(k: KpiCard): { label: string; tone: StatusTone } {
    // A "met / total" card is judged on its own figure, on the platform's score bands — never on
    // the standard's colour, which made every Cyber Essentials card "On track" (AUD-46).
    const m = k.value.match(/^(\d+) \/ (\d+)$/);
    if (m && Number(m[2]) > 0) {
      const band = scoreBand(Math.round((Number(m[1]) / Number(m[2])) * 100));
      if (band === "strong" || band === "compliant") return { label: "On track", tone: "met" };
      if (band === "partial") return { label: "In progress", tone: "partial" };
      return { label: "Needs attention", tone: "gap" };
    }
    const color = k.color;
    if (color === cbSafe("#22c55e") || color === cbSafe("#22C55E") || color === FRAMEWORK_COLORS["cyber_essentials"] || color === FRAMEWORK_COLORS["cyber_essentials_plus"])
      return { label: "On track", tone: "met" };
    if (color === "#f59e0b" || color === "#ca8a04" || color === FRAMEWORK_COLORS["iso9001"] || color === FRAMEWORK_COLORS["soc2"])
      return { label: "In progress", tone: "partial" };
    if (color === cbSafe("#dc2626") || color === FRAMEWORK_COLORS["pci_dss"])
      return { label: "Needs attention", tone: "gap" };
    return { label: "Tracked", tone: "none" };
  }

  return (
    <div style={{ padding: isMobile ? "14px 12px" : "20px 24px" }}>

      {/* ── API error banner ────────────────────────────────────────── */}
      {loadError && (
        <div style={{
          marginBottom: 16, padding: "12px 16px",
          background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)",
          borderRadius: 3, fontSize: 13, color: "var(--c-dangerText)",
        }}>
          <strong>Could not load dashboard data</strong> — {loadError}. The API may be temporarily unavailable; try refreshing in a moment.
        </div>
      )}

      {/* ── Get started prompt (no frameworks, no legal regulations configured) ── */}
      {!frameworksLoading && enrolled.size === 0 && !(legalSummary && legalSummary.regulations.length > 0) && (
        <div style={{ ...card, marginBottom: 16, background: "var(--c-accentBg)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)", marginBottom: 4 }}>Get started with Abacas</div>
          <div style={{ fontSize: 12, color: "var(--c-subtle)", marginBottom: 14 }}>
            Enrol in a compliance framework, or use the platform to manage your legal and regulatory obligations independently — no standard required.
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button onClick={() => navigate("/my-services")} style={btnPrimary}>Select frameworks</button>
            <button onClick={() => navigate("/org-profile")} style={btnSecondary}>Manage legal obligations only</button>
          </div>
        </div>
      )}

      {/* ── Pending check ────────────────────────────────────────────── */}
      {pendingRun && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "11px 16px", marginBottom: 14, borderRadius: 3,
          background: "var(--c-accentBg)", border: "1px solid var(--c-border)", fontSize: 13,
        }}>
          <Clock size={16} color="var(--c-accentText)" style={{ flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: "var(--c-heading)" }}>Compliance check {pendingRun.status}…</span>
          <span style={{ color: "var(--c-subtle)", fontSize: 12 }}>Results will appear once complete — usually under a minute.</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate("/compliance")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--c-accentText)", whiteSpace: "nowrap" }}>View status →</button>
        </div>
      )}

      {/* ── Spend cap alert ──────────────────────────────────────────── */}
      {capHit && (
        <div style={{
          display: "flex", alignItems: "center", gap: 12,
          marginBottom: 14, padding: "11px 16px",
          background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", borderRadius: 3,
        }}>
          <Ban size={18} color="var(--c-dangerText)" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-dangerText)" }}>
              Monthly spend cap of £{(spendCapPence / 100).toFixed(2)} reached
            </div>
            <div style={{ fontSize: 11, color: "var(--c-danger)", marginTop: 2 }}>
              Ad-hoc checks and consultancy bookings are disabled until end of month. Scheduled AI checks continue normally.
            </div>
          </div>
          <button onClick={() => navigate("/my-services")} style={{ ...btnPrimary, background: "var(--c-danger)", flexShrink: 0, whiteSpace: "nowrap" }}>
            Adjust cap →
          </button>
        </div>
      )}

      {/* ── Actions tile ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: 14 }}>
        <ActionsTile endpoint="/actions" onClick={() => navigate("/actions")} />
      </div>

      {/* ── Framework filter ─────────────────────────────────────────── */}
      <FrameworkFilter
        frameworks={frameworks}
        hidden={hiddenFw}
        onToggle={(id) => setHiddenFw((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; })}
        onShowAll={() => setHiddenFw(new Set())}
      />

      {/* ── KPI grid ────────────────────────────────────────────────── */}
      {kpis.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 14 }}>
          {kpis.map((k) => {
            const clickable = !!k.link;
            const pill = kpiPill(k);
            return (
              <div
                key={k.label}
                onClick={() => k.link && navigate(k.link)}
                style={{
                  ...card,
                  padding: "16px 18px",
                  cursor: clickable ? "pointer" : "default",
                  transition: "border-color 0.14s, transform 0.13s",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                }}
                onMouseEnter={(e) => { if (clickable) { (e.currentTarget as HTMLElement).style.borderColor = "var(--c-borderStrong)"; (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)"; } }}
                onMouseLeave={(e) => { if (clickable) { (e.currentTarget as HTMLElement).style.borderColor = "var(--c-line)"; (e.currentTarget as HTMLElement).style.transform = "none"; } }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--c-muted)", lineHeight: 1.3 }}>{k.label}</div>
                  <StatusTag tone={pill.tone}>{pill.label}</StatusTag>
                </div>
                <div>
                  <div style={{ fontSize: k.small ? 16 : 28, fontWeight: 800, color: "var(--c-text)", letterSpacing: k.small ? 0 : "-1px", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {k.value}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--c-muted)", marginTop: 4 }}>{k.sub}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Risk severity breakdown ──────────────────────────────────── */}
      {riskSummary.length > 0 && (() => {
        const counts = countBySeverity(riskSummary);
        const total = riskSummary.length;
        return (
          <div
            style={{ ...card, marginBottom: 14, cursor: "pointer" }}
            onClick={() => navigate("/risk-register")}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Risk Register</div>
              <span style={{ fontSize: 12, color: "var(--c-accentText)", fontWeight: 600 }}>View register →</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 10 }}>
              {SEVERITY_ORDER.map(sev => {
                const meta = SEVERITY_META[sev];
                const count = counts[sev];
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={sev} style={{ background: meta.bg, borderRadius: 3, padding: "10px 14px", border: `1px solid ${meta.text}30` }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: meta.text, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{count}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: meta.text, marginTop: 2 }}>{meta.label}</div>
                    <div style={{ fontSize: 10, color: "var(--c-subtle)", marginTop: 1 }}>score {meta.range} · {pct}%</div>
                    {/* Thin fill bar */}
                    <div style={{ marginTop: 8, height: 3, background: `${meta.text}30`, borderRadius: 2 }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: meta.text, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── Two-column main content ──────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 330px", gap: 12, alignItems: "start" }}>

        {/* ── Left column ─────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Compliance Position */}
          {complianceBars.length > 0 && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Compliance Position</div>
                <button onClick={() => navigate("/executive-report")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--c-accentText)", fontWeight: 600 }}>Full report →</button>
              </div>
              {complianceBars.map((bar) => (
                <div key={bar.label} style={{ marginBottom: 13 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12.5, color: "var(--c-text)" }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: bar.color, flexShrink: 0, display: "inline-block" }} />
                      {bar.label}
                    </span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--c-text)", fontVariantNumeric: "tabular-nums" }}>{bar.pct}%</span>
                  </div>
                  <RulerProgress value={barsVisible ? bar.pct : 0} fill={bar.color} label={bar.label} />
                </div>
              ))}
            </div>
          )}

          {/* Quick Actions */}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)", marginBottom: 14 }}>Quick Actions</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 9 }}>
              {quickActions.map((a) => (
                <button
                  key={a.to}
                  onClick={() => navigate(a.to)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "11px 13px",
                    borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panelAlt)",
                    cursor: "pointer", textAlign: "left", transition: "border-color 0.12s, background 0.12s, transform 0.12s",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--c-panel)"; (e.currentTarget as HTMLElement).style.borderColor = "var(--c-borderStrong)"; (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--c-panelAlt)"; (e.currentTarget as HTMLElement).style.borderColor = "var(--c-border)"; (e.currentTarget as HTMLElement).style.transform = "none"; }}
                >
                  <span style={{ width: 32, height: 32, borderRadius: 3, background: "var(--c-accentBg)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>{a.icon}</span>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--c-text)", lineHeight: 1.3 }}>{a.label}</div>
                    <div style={{ fontSize: 10.5, color: "var(--c-muted)", marginTop: 1 }}>{a.sub}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Billing & Usage */}
          {canBilling && (<div style={card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)", marginBottom: 14 }}>Billing & Usage</div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
              <div style={{ background: "var(--c-panelAlt)", borderRadius: 3, padding: "13px 15px", border: "1px solid var(--c-border)" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Monthly spend</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: "var(--c-accentText)", lineHeight: 1, marginBottom: 4, fontVariantNumeric: "tabular-nums" }}>
                  £{((platformFeePence + consultancySpendPence + aiSpendPence) / 100).toFixed(2)}
                </div>
                <div style={{ fontSize: 11, color: "var(--c-subtle)", lineHeight: 1.5 }}>
                  {fwCount} standard{fwCount !== 1 ? "s" : ""} £{(standardsFeePence / 100).toFixed(2)}
                  {tierFeePence > 0 && ` + £${(tierFeePence / 100).toFixed(2)} platform`}
                  {integrationFeePence > 0 && ` + £${(integrationFeePence / 100).toFixed(2)} integrations`}
                  {tprmFeePence > 0 && ` + £${(tprmFeePence / 100).toFixed(2)} supplier risk`}
                  {consultancySpendPence > 0 && ` + £${(consultancySpendPence / 100).toFixed(2)} consultancy`}
                  {aiSpendPence > 0 && ` + £${(aiSpendPence / 100).toFixed(2)} AI`}
                </div>
                <button onClick={() => navigate("/my-services")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--c-accentText)", fontWeight: 600, marginTop: 8 }}>
                  Manage billing →
                </button>
              </div>

              <div style={{ background: "var(--c-panelAlt)", borderRadius: 3, padding: "13px 15px", border: `1px solid ${capHit ? "var(--c-danger)" : "var(--c-border)"}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--c-faint)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>Spend cap</div>
                {spendCapPence <= 0 ? (
                  <>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--c-muted)", marginBottom: 4 }}>No cap set</div>
                    <div style={{ fontSize: 11, color: "var(--c-subtle)" }}>AI usage is uncapped.</div>
                    <button onClick={() => navigate("/my-services")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--c-accentText)", fontWeight: 600, marginTop: 8 }}>Set a cap →</button>
                  </>
                ) : (() => {
                  const pct = Math.min(100, Math.round((aiSpendPence / spendCapPence) * 100));
                  const capColor = capHit ? "var(--c-danger)" : pct >= 80 ? "var(--c-warning)" : "var(--c-success)";
                  return (
                    <>
                      <div style={{ fontSize: 18, fontWeight: 700, color: capColor, lineHeight: 1, marginBottom: 6, fontVariantNumeric: "tabular-nums" }}>
                        {capHit ? "Cap reached" : `£${Math.max(0, (spendCapPence - aiSpendPence) / 100).toFixed(2)} remaining`}
                      </div>
                      <RulerProgress value={pct} fill={capColor} label="Spend cap used" style={{ marginBottom: 6 }} />
                      <div style={{ fontSize: 11, color: "var(--c-subtle)", fontVariantNumeric: "tabular-nums" }}>
                        £{(aiSpendPence / 100).toFixed(2)} of £{(spendCapPence / 100).toFixed(2)} ({pct}%)
                      </div>
                      <button onClick={() => navigate("/my-services")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: capHit ? "var(--c-danger)" : "var(--c-accentText)", fontWeight: 600, marginTop: 8 }}>
                        {capHit ? "Raise cap →" : "Adjust cap →"}
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>)}
        </div>

        {/* ── Right column ─────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

          {/* Auditor Findings */}
          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: sortedFindings.length > 0 ? 12 : 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Auditor Findings</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {actionCount > 0 && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 2, background: "var(--c-dangerBg)", color: "var(--c-dangerText)" }}>
                    {actionCount} open
                  </span>
                )}
                {sortedFindings.length > 0 && (
                  <button onClick={() => navigate("/auditor-feedback")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--c-accentText)", fontWeight: 600 }}>View all →</button>
                )}
              </div>
            </div>
            {sortedFindings.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--c-subtle)", fontStyle: "italic", paddingTop: 4 }}>
                No auditor feedback yet.
              </div>
            ) : (
              <div style={{ margin: "0 -4px" }}>
                {sortedFindings.slice(0, 4).map((f, i) => {
                  const cfg = FINDING_STATUS[f.status] ?? { label: f.status, color: "var(--c-muted)", bg: "transparent", border: "var(--c-border)" };
                  const isLast = i === Math.min(sortedFindings.length, 4) - 1;
                  return (
                    <div key={f.id} style={{
                      display: "flex", alignItems: "flex-start", gap: 9,
                      padding: "10px 4px",
                      borderBottom: isLast ? "none" : "1px solid var(--c-border)",
                    }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color, flexShrink: 0, marginTop: 4 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: f.text ? 4 : 0 }}>
                          <button
                            onClick={e => { e.stopPropagation(); navigate(controlRoute(f.controlId, f.frameworkId)); }}
                            style={{ fontSize: 11, fontWeight: 700, color: "var(--c-accentText)", fontVariantNumeric: "tabular-nums", background: "var(--c-accentBg)", border: "none", borderRadius: 4, padding: "1px 5px", cursor: "pointer" }}
                          >
                            {controlRefWithStandard(f.controlId) ?? f.controlId}
                          </button>
                          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 2, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>
                          {f.createdAt && <span style={{ fontSize: 10, color: "var(--c-faint)", marginLeft: "auto" }}>{fmtShortDate(f.createdAt)}</span>}
                        </div>
                        {f.text && (
                          <div style={{ fontSize: 12, color: "var(--c-subtle)", lineHeight: 1.5 }}>
                            {f.text.length > 120 ? f.text.slice(0, 120) + "…" : f.text}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Needs Your Attention (overdue) */}
          {overdueEvents.length > 0 && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Overdue</div>
                <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 7px", borderRadius: 2, background: "var(--c-dangerBg)", color: "var(--c-dangerText)" }}>
                  {overdueEvents.length} overdue
                </span>
              </div>
              {overdueEvents.slice(0, 4).map((e, i) => {
                const late = daysLate(e.eventDate);
                const isLast = i === Math.min(overdueEvents.length, 4) - 1;
                return (
                  <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: isLast ? "none" : "1px solid var(--c-border)" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-text)" }}>{e.title}</div>
                      <div style={{ fontSize: 11, color: "var(--c-faint)", marginTop: 1 }}>{KIND_LABEL[e.kind] ?? e.kind}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--c-danger)" }}>{late}d late</div>
                      <div style={{ fontSize: 10, color: "var(--c-faint)", marginTop: 1 }}>{fmtShortDate(e.eventDate)}</div>
                    </div>
                  </div>
                );
              })}
              {overdueEvents.length > 4 && (
                <button onClick={() => navigate("/schedule")} style={{ background: "none", border: "none", padding: "8px 0 0", cursor: "pointer", fontSize: 12, color: "var(--c-accentText)" }}>
                  +{overdueEvents.length - 4} more →
                </button>
              )}
            </div>
          )}

          {/* Upcoming events */}
          {upcomingEvents.length > 0 && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Upcoming</div>
                <button onClick={() => navigate("/schedule")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--c-accentText)", fontWeight: 600 }}>View all →</button>
              </div>
              {upcomingEvents.slice(0, 4).map((e, i) => {
                const d = new Date(e.eventDate);
                const day = d.toLocaleDateString("en-GB", { day: "2-digit" });
                const mon = d.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
                const isLast = i === Math.min(upcomingEvents.length, 4) - 1;
                return (
                  <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 11, padding: "9px 0", borderBottom: isLast ? "none" : "1px solid var(--c-border)" }}>
                    <div style={{ width: 38, height: 38, borderRadius: 3, background: "var(--c-accentBg)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 14, fontWeight: 800, color: "var(--c-accentText)", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{day}</span>
                      <span style={{ fontSize: 9, fontWeight: 700, color: "var(--c-accentText)", letterSpacing: "0.4px", marginTop: 1 }}>{mon}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-text)" }}>{e.title}</div>
                      <div style={{ fontSize: 11, color: "var(--c-muted)", marginTop: 1 }}>{KIND_LABEL[e.kind] ?? e.kind}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Latest check */}
          {latestRun && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Last AI Check</div>
                <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 7px", borderRadius: 2, background: "var(--c-panelAlt)", border: "1px solid var(--c-border)", color: "var(--c-muted)" }}>
                  {new Date(latestRun.completedAt ?? latestRun.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                {latestRun.overallScore != null && (
                  <svg width="54" height="54" viewBox="0 0 54 54" style={{ flexShrink: 0 }}>
                    <circle cx="27" cy="27" r="22" fill="none" stroke="var(--c-border)" strokeWidth="4"/>
                    <circle cx="27" cy="27" r="22" fill="none" stroke={scoreColor(latestRun.overallScore)} strokeWidth="4"
                      strokeDasharray="138.2"
                      strokeDashoffset={138.2 * (1 - latestRun.overallScore / 100)}
                      strokeLinecap="round" transform="rotate(-90 27 27)" opacity="0.85"/>
                    <text x="27" y="27" textAnchor="middle" dominantBaseline="middle"
                      fontSize="13" fontWeight="800" fill="var(--c-text)" fontFamily={theme.font}>
                      {latestRun.overallScore}%
                    </text>
                  </svg>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--c-text)", marginBottom: 3 }}>
                    Overall score: {latestRun.overallScore ?? "—"}/100
                  </div>
                  <button onClick={() => navigate("/compliance")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 12, color: "var(--c-accentText)", fontWeight: 600 }}>
                    View full results →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Score history sparkline — shown when ≥2 completed runs exist */}
          {(() => {
            const history = runs
              .filter((r) => r.status === "done" && r.overallScore !== null)
              .sort((a, b) => new Date(a.completedAt ?? a.createdAt).getTime() - new Date(b.completedAt ?? b.createdAt).getTime())
              .slice(-10);
            if (history.length < 2) return null;

            const W = 260, H = 56, PAD = 6;
            const scores = history.map((r) => r.overallScore as number);
            const minS = Math.max(0, Math.min(...scores) - 10);
            const maxS = Math.min(100, Math.max(...scores) + 10);
            const range = maxS - minS || 1;

            const toX = (i: number) => PAD + (i / (history.length - 1)) * (W - PAD * 2);
            const toY = (s: number) => PAD + (1 - (s - minS) / range) * (H - PAD * 2);

            const pts = history.map((r, i) => `${toX(i)},${toY(r.overallScore as number)}`).join(" ");
            const latest = scores[scores.length - 1];
            const first  = scores[0];
            const trend  = latest - first;
            const trendColor = trend > 0 ? "var(--c-success)" : trend < 0 ? "var(--c-danger)" : "var(--c-accent)";
            const trendLabel = trend > 0 ? `+${trend}` : trend < 0 ? `${trend}` : "—";

            const fmtShort = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });

            return (
              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--c-heading)" }}>Score History</div>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 2,
                    background: trend > 0 ? "var(--c-successBg)" : trend < 0 ? "var(--c-dangerBg)" : "var(--c-panelAlt)",
                    color: trendColor,
                  }}>
                    {trendLabel} pts
                  </span>
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: 56, display: "block" }}>
                  {/* Grid lines at 25 / 50 / 75 */}
                  {[25, 50, 75].map((v) => {
                    if (v < minS || v > maxS) return null;
                    const y = toY(v);
                    return (
                      <line key={v} x1={PAD} y1={y} x2={W - PAD} y2={y}
                        stroke="var(--c-border)" strokeWidth="0.5" strokeDasharray="3 3" />
                    );
                  })}
                  {/* Area fill */}
                  <polyline
                    points={`${toX(0)},${H - PAD} ${pts} ${toX(history.length - 1)},${H - PAD}`}
                    fill={trendColor} fillOpacity="0.08" stroke="none"
                  />
                  {/* Line */}
                  <polyline points={pts} fill="none" stroke={trendColor} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
                  {/* Dots */}
                  {history.map((r, i) => (
                    <circle key={r.id} cx={toX(i)} cy={toY(r.overallScore as number)} r="2.5"
                      fill={trendColor} stroke="var(--c-panel)" strokeWidth="1.5">
                      <title>{`${fmtShort(r.completedAt ?? r.createdAt)}: ${r.overallScore}%`}</title>
                    </circle>
                  ))}
                </svg>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontSize: 10, color: "var(--c-faint)", fontVariantNumeric: "tabular-nums" }}>
                  <span>{fmtShort(history[0].completedAt ?? history[0].createdAt)} · {first}%</span>
                  <span>{history.length} checks</span>
                  <span>{fmtShort(history[history.length - 1].completedAt ?? history[history.length - 1].createdAt)} · {latest}%</span>
                </div>
              </div>
            );
          })()}

        </div>
      </div>
    </div>
  );
}
